import { action, observable, computed, reaction } from 'mobx';
import get from 'lodash/get';
import UserSettings from './user-settings';
import { sortGuestsByEventInvitation, sortGuestsByName } from '../../utils/sort';

export default class GdsUiState {
  @observable filter = '';
  @observable selectedEvent;
  @observable allGuestsView = false;
  @observable activeModalComponent;
  @observable activeModalConfirmation = false;
  @observable editingHousehold;
  @observable editingGuest;
  @observable filteredAndSortedGuestList = [];
  @observable filteredAndSortedHouseholdList = [];
  @observable showBulkDeleteConfirmation;
  @observable addressCollectionView;
  @observable rsvpModalView = false;
  @observable exportGuestModalView = false;
  @observable messageGuestModalView = false;
  @observable rsvpFilteredEvent;
  @observable rsvpFilter;
  @observable mealFilter;
  @observable addressFilter = false;
  @observable rsvpBulkSelectedEvent;
  @observable rsvpBulkSelection;
  @observable mealBulkSelection;
  @observable filterResultsLength;
  @observable highlightedEventColumn;
  @observable sortSignal = false;
  @observable householdToggle = true;
  @observable showAppDialog = false;
  @observable showEventModal = false;
  @observable showHouseholdForm = false;
  @observable householdFormDefaultState = 'main';
  @observable userSettings = new UserSettings();
  @observable selectedEventColumns = [
    'Invite Sent',
    'RSVP',
    'Meal',
    'Contact Info',
    'Notes',
    'Gift',
    'Thank You Sent',
    'RSVP Details',
  ];
  @observable selectedAllGuestColumns = [];
  @observable sort = {
    asc: true,
    direction: null,
    sortBy: 'guest',
    attribute: '',
    eventId: null,
  };
  urlEventId;
  userStore;
  appDialog;
  showMessagingHub;
  snackbarRef;

  constructor(userStore = {}, history = {}) {
    this.userStore = userStore;
    this.history = history;
  }

  setupInitialEvents(eventIds) {
    this.selectedAllGuestColumns = eventIds;
  }

  setSnackbarRef(ref) {
    this.snackbarRef = ref;
  }

  @action
  showSnackbar(message, dismissTime = 20000) {
    if (this.snackbarRef) {
      this.snackbarRef.current.showAndDismiss({
        data: message,
        dismissTimeout: dismissTime,
      });
    }
  }

  @action
  showDialog(component) {
    if (component) {
      this.appDialog = component;
      this.showAppDialog = true;
    }
  }

  @action
  closeDialog() {
    this.appDialog = null;
    this.showAppDialog = false;
  }

  @action
  setFilter(strValue) {
    this.filter = strValue;
  }

  @action
  setFilterResultsLength(length) {
    this.filterResultsLength = length;
  }

  @action
  setHighlightedEventColumn(event) {
    this.highlightedEventColumn = event;
  }

  @action
  initUrlParams({ event, filter = '', household = true }) {
    if (event === 'all') {
      this.allGuestsView = true;
    } else {
      this.urlEventId = parseInt(event, 10);
    }

    this.filter = filter;
    this.householdToggle = household;
  }

  @action
  goToAllGuestsView({ householdToggle = true, message }) {
    this.selectedEvent = null;
    this.allGuestsView = true;
    this.householdToggle = householdToggle;
    this.deselectGuests();
    this.addressCollectionView = false;
    this.rsvpSettingsView = false;
    this.importSpreadsheetView = false;
    this._updateBrowserHistory(message);
  }

  setUrlEventId(id) {
    if (id === 'all') {
      this.setSelectedEvent();
    } else {
      this.urlEventId = parseInt(id, 10);
    }
  }

  @action
  clearDeleteConfirmation = () => {
    this.showBulkDeleteConfirmation = false;
  };

  @computed
  get viewType() {
    if (this.addressCollectionView) {
      return 'address collection';
    } else if (this.rsvpSettingsView) {
      return 'rsvp settings';
    } else if (this.selectedEvent) {
      return 'event';
    } else if (this.allGuestsView && this.householdToggle) {
      return 'all events';
    } else if (this.importSpreadsheetView) {
      return 'import spreadsheet';
    }

    return 'all individuals';
  }

  @action
  triggerBulkDeleteConfirmation() {
    this.showBulkDeleteConfirmation = true;
    setTimeout(this.clearDeleteConfirmation, 3000);
  }

  @computed
  get querying() {
    return this.filter !== '';
  }

  @action
  triggerSort() {
    this.sortSignal = !this.sortSignal;
  }

  computeSortOrder(sortAttrs) {
    if (
      sortAttrs.sortBy === this.sort.sortBy &&
      sortAttrs.attribute === this.sort.attribute &&
      sortAttrs.eventId === this.sort.eventId
    ) {
      return !this.sort.asc;
    }

    return true;
  }

  @action
  setSelectedEvent = event => {
    this.clearCurrentView();
    if (event) {
      this.allGuestsView = false;
    } else {
      this.allGuestsView = true;
    }
    this.selectedEvent = event;
    this._updateBrowserHistory();
  };

  clearSelectedEvent() {
    this.setSelectedEvent(null);
  }

  @action
  setShowEventModal = showEventModal => {
    this.showEventModal = showEventModal;
  };

  @action
  setShowHouseholdForm(showHouseholdForm) {
    this.showHouseholdForm = showHouseholdForm;
  }

  @action
  setHouseholdFormDefaultState(state) {
    this.householdFormDefaultState = state;
  }

  @action
  setAllGuestsView = (householdToggle = true) => {
    this.clearCurrentView();
    this.allGuestsView = true;
    this.householdToggle = householdToggle;
    this._updateBrowserHistory();
  };

  @computed
  get allHouseholdPage() {
    return this.allGuestsView && this.householdToggle;
  }

  @computed
  get guestCount() {
    return this.filteredGuests.length;
  }

  @computed
  get filteredGuests() {
    const filterBySelectedEvent = guest => {
      if (!this.selectedEvent || !this.selectedEvent.id || this.allGuestsView) return true;

      return !!guest.invitations.find(invitation => invitation.eventId === this.selectedEvent.id);
    };

    const filterBySearch = guest => {
      const searchVal = this.filter.toLowerCase();
      return (
        !this.filter ||
        guest.selected ||
        (guest.firstName || '').toLowerCase().includes(searchVal) ||
        (guest.lastName || '').toLowerCase().includes(searchVal) ||
        `${guest.firstName} ${guest.lastName}`.toLowerCase().includes(searchVal)
      );
    };

    const filterByRSVP = guest => {
      if (!this.rsvpFilter) return true;
      const rsvpMap = {
        Invited: null,
        Attending: true,
        Declined: false,
      };
      const filterEvent = this.selectedEvent ? this.selectedEvent : this.rsvpFilteredEvent;
      const eventInvitation = guest.invitations.find(invitation => {
        return invitation.eventId === get(filterEvent, ['id']);
      });

      return eventInvitation && rsvpMap[this.rsvpFilter] === eventInvitation.rsvp;
    };

    const filterByMeal = guest => {
      if (!this.mealFilter) return true;

      const filterEvent = this.selectedEvent ? this.selectedEvent : this.rsvpFilteredEvent;
      const eventInvitation = guest.invitations.find(invitation => {
        return invitation.eventId === get(filterEvent, ['id']);
      });

      return this.mealFilter === eventInvitation.meal;
    };

    return this.guestCollection.allGuests.filter(
      guest => filterBySelectedEvent(guest) && filterBySearch(guest) && filterByRSVP(guest) && filterByMeal(guest)
    );
  }

  @computed
  get filteredHouseholds() {
    if (!this.addressFilter) return this.guestCollection;

    return this.guestCollection.filter(household => !household.street1 && !household.city && !household.state);
  }

  @action
  setSort(sortAttrs) {
    this.sort.asc = typeof sortAttrs.asc !== 'undefined' ? sortAttrs.asc : this.computeSortOrder(sortAttrs);
    this.sort.sortBy = sortAttrs.sortBy;
    this.sort.attribute = sortAttrs.attribute;
    this.sort.eventId = sortAttrs.eventId;
    this.sort.direction = sortAttrs.direction;
    this.triggerSort();
  }

  @action
  setRsvpSettingsView(value) {
    this.clearCurrentView();
    this.rsvpSettingsView = value;
  }

  @action
  setRsvpModalView(viewState) {
    this.rsvpModalView = viewState;
  }

  @action
  setExportGuestModalView(viewState) {
    this.exportGuestModalView = viewState;
  }

  @action
  setMessageGuestModalView(value) {
    this.messageGuestModalView = value;
  }

  @action
  setRsvpFilter(status, event) {
    this.rsvpFilter = status;
    this.rsvpFilteredEvent = event;
  }

  @action
  setMealFilter(strValue) {
    this.mealFilter = strValue;
  }

  @action
  setAddressFilter(boolValue) {
    this.addressFilter = boolValue;
  }

  @action
  setRsvpBulkSelection(status, event) {
    this.rsvpBulkSelection = status;
    this.rsvpBulkSelectedEvent = event;
  }

  @action
  setMealBulkSelection(value) {
    this.mealBulkSelection = value;
  }

  @action
  clearBulkUpdateValues() {
    this.rsvpBulkSelection = null;
    this.mealBulkSelection = null;
    this.rsvpBulkSelectedEvent = null;
  }

  @action
  setAddressCollectionView(value) {
    this.clearCurrentView();
    this.addressCollectionView = value;
  }

  @action
  setImportSpreadsheetView(value) {
    this.clearCurrentView();
    this.importSpreadsheetView = value;
  }

  @action
  clearCurrentView = () => {
    this.selectedEvent = undefined;
    this.addressCollectionView = undefined;
    this.rsvpSettingsView = undefined;
    this.importSpreadsheetView = undefined;
    this.allGuestsView = false;
  };

  @action
  setEditingGuest(guest) {
    this.editingGuest = guest;
  }

  @action
  setEditingHousehold(household) {
    this.editingHousehold = household;
  }

  @action
  setActiveModalComponent(modalComponent) {
    this.activeModalComponent = modalComponent;
  }

  @action
  setShowMessagingHub(shouldShow) {
    this.showMessagingHub = shouldShow;
  }

  @action
  showModalConfirmation() {
    this.activeModalConfirmation = true;
  }

  @action
  hideModalConfirmation() {
    this.activeModalConfirmation = false;
  }

  @action
  closeActiveModal = () => {
    this.activeModalComponent = null;
    this.activeModalConfirmation = false;
    this.editingGuest = null;
    this.editingHousehold = null;
  };

  @action
  closeConfirmationModal() {
    this.activeModalConfirmation = false;
  }
  /* this functionality still needs to be built out in the sortable story */

  isColumnVisible(columnName) {
    const eventId = this.selectedEvent ? this.selectedEvent.id : null;
    if (eventId) {
      return this.isSelectedEventColumnVisible(columnName);
    }
    return false;
  }

  isEventColumnVisible = (eventId = '') => {
    return this.selectedAllGuestColumns.includes(eventId);
  };

  isSelectedEventColumnVisible = (columnName = '') => {
    if (columnName === 'RSVP Details') {
      return this.userStore.hasWWS;
    }

    return this.selectedEventColumns.includes(columnName);
  };

  @action
  toggleSelectedEventColumnVisible = columnName => {
    if (this.isSelectedEventColumnVisible(columnName)) {
      this.selectedEventColumns.remove(columnName);
    } else {
      this.selectedEventColumns.push(columnName);
    }
  };

  @action
  toggleEventColumnVisible = columnName => {
    if (this.isEventColumnVisible(columnName)) {
      this.selectedAllGuestColumns.remove(columnName);
    } else {
      this.selectedAllGuestColumns.push(columnName);
    }
  };

  @action
  toggleColumnVisible = columnName => {
    const eventId = this.selectedEvent ? this.selectedEvent.id : null;
    if (eventId) {
      this.toggleSelectedEventColumnVisible(columnName);
    }
    this.toggleEventColumnVisible(columnName);
  };

  isEventColumnVisible(eventId) {
    return this.userSettings.isEventColumnVisible(eventId);
  }

  toggleColumnVisibility(columnName) {
    const eventId = this.selectedEvent ? this.selectedEvent.id : null;
    this.userSettings.toggleColumnVisibility(this.allGuestsView, eventId, columnName);
  }

  toggleEventColumnVisibility(eventId) {
    this.userSettings.toggleEventColumnVisibility(eventId);
  }

  toggleAllGuestsSelection() {
    const value = !this.allGuestsSelected;
    this.filteredAndSortedGuestList.forEach(g => g.setSelected(value));
    return true;
  }

  get allGuestsSelected() {
    return !!this.filteredAndSortedGuestList.length && this.filteredAndSortedGuestList.every(guest => guest.selected);
  }

  get anyGuestsSelected() {
    return !!this.filteredAndSortedGuestList.length && this.filteredAndSortedGuestList.some(guest => guest.selected);
  }

  get selectedGuests() {
    return this.filteredAndSortedGuestList.filter(guest => guest.selected);
  }

  get numberOfGuestsSelected() {
    return this.selectedGuests.length;
  }

  get isBulkSelecting() {
    return this.anyGuestsSelected || this.showBulkDeleteConfirmation;
  }

  @computed
  get rsvpFiltering() {
    return !!this.rsvpFilter;
  }

  @computed
  get mealFiltering() {
    return !!this.mealFilter;
  }

  deselectGuests() {
    this.filteredAndSortedGuestList.forEach(g => {
      if (g.selected) g.setSelected(false);
    });
  }

  setupReactionsOnGuestListChanges = () => {
    reaction(
      () => this.filteredGuests,
      filteredGuests => {
        this.filteredAndSortedGuestList = filteredGuests;
        if (!this.householdToggle) {
          this.triggerSort();
        }
      },
      { fireImmediately: true, name: 'refreshFilteredGuests' }
    );

    reaction(
      () => this.filteredHouseholds,
      filteredHouseholds => {
        this.filteredAndSortedHouseholdList = filteredHouseholds;
        if (this.householdToggle) {
          this.triggerSort();
        }
      },
      { fireImmediately: true, name: 'refreshFilteredHouseholds' }
    );

    reaction(
      () => this.sortSignal,
      () => {
        if (!this.householdToggle) {
          this.filteredAndSortedGuestList = this.sortGuestList(this.filteredAndSortedGuestList);
        }
      },
      { fireImmediately: true, name: 'refreshSortedGuests' }
    );
  };

  sortGuestList(guests) {
    switch (this.sort.sortBy) {
      case 'invitation':
        return sortGuestsByEventInvitation(guests, {
          eventId: this.sort.eventId,
          sortAttr: this.sort.attribute,
          asc: this.sort.asc,
        });
      case 'guest':
        return sortGuestsByName(guests, {
          sortAttr: this.sort.attribute,
          asc: this.sort.asc,
        });
      default:
        return sortGuestsByName(guests, {
          sortAttr: this.sort.attribute,
          asc: this.sort.asc,
        });
    }
  }

  /* the above functionality still needs to be built out */
  _updateBrowserHistory(message = null) {
    let eventId;
    if (this.allGuestsView) {
      eventId = 'all';
    } else if (this.selectedEvent) {
      eventId = this.selectedEvent.id;
    } else {
      eventId = this.urlEventId;
    }
    this.history.push(`/our-guest-list/?event=${eventId}&party=${this.householdToggle}`, {
      state: { message },
    });
  }
}
